import type { Comment, CommentPage, CommentRepliesRef, CreatorHeart } from '../model/comment.ts';
import { err, ok, parseError, type ParsedList, type ParseError, type Result } from '../result.ts';
import type { ParseContext } from './context.ts';
import { parseImageList, parseImageUrl } from './image.ts';
import { asArray, asBoolean, asNonEmptyString, asNumber, asObject, asString, field } from './raw.ts';
import { parseRichTextHtml } from './richText.ts';
import { asEpochSeconds } from './time.ts';

function parseCreatorHeart(raw: unknown, ctx: ParseContext): CreatorHeart | null {
  const source = asObject(raw);
  if (!source) return null;
  const creatorName = asNonEmptyString(field(source, 'creatorName'));
  if (creatorName === null) return null;
  return { creatorName, thumbnail: parseImageUrl(field(source, 'creatorThumbnail'), ctx) };
}

/**
 * The continuation is the whole point of this object: without it the replies cannot be fetched at
 * all, since they are never inlined. A `replies` block that has lost its token is therefore no
 * `replies` block — reporting "961 replies" behind a button that cannot load them is worse than
 * reporting none.
 */
function parseRepliesRef(raw: unknown): CommentRepliesRef | null {
  const source = asObject(raw);
  if (!source) return null;
  const continuation = asNonEmptyString(field(source, 'continuation'));
  if (continuation === null) return null;
  return { replyCount: asNumber(field(source, 'replyCount')), continuation };
}

/**
 * R4 — one comment, from either `/api/v1/comments/{videoId}` or a replies page. The two are the
 * same record; a reply simply never carries `replies` or `creatorHeart`.
 *
 * `commentId` is required because it is the React key, the reply anchor and the only stable
 * identity a comment has. Everything else degrades: `authorThumbnails` is an **empty array** on
 * every entry of the committed replies page, so an avatar-less comment is normal, not broken.
 *
 * R7: `publishedText` is dropped — this instance answers `"1 السنة منذ"`, i.e. the server's locale,
 * not the viewer's. `published` is epoch seconds and is the only portable form.
 */
export function parseComment(raw: unknown, ctx: ParseContext): Result<Comment, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('Comment', null, 'wrong-type'));

  const commentId = asNonEmptyString(field(source, 'commentId'));
  if (commentId === null) return err(parseError('Comment', 'commentId', 'missing'));

  return ok({
    commentId,
    author: asString(field(source, 'author')) ?? '',
    authorId: asNonEmptyString(field(source, 'authorId')),
    authorUrl: asNonEmptyString(field(source, 'authorUrl')),
    authorThumbnails: parseImageList(field(source, 'authorThumbnails'), ctx),
    // Note the spelling: a comment's is `verified`, a search record's is `authorVerified`.
    authorVerified: asBoolean(field(source, 'verified')) ?? false,
    authorIsChannelOwner: asBoolean(field(source, 'authorIsChannelOwner')) ?? false,
    isSponsor: asBoolean(field(source, 'isSponsor')) ?? false,
    isPinned: asBoolean(field(source, 'isPinned')) ?? false,
    isEdited: asBoolean(field(source, 'isEdited')) ?? false,
    likeCount: asNumber(field(source, 'likeCount')),
    content: asString(field(source, 'content')) ?? '',
    // R8 — comments are the one place the *HTML* is the better source: its anchor boundaries are
    // honest here, and it is the only carrier of a mention's channel ID and a timestamp's target.
    bodyNodes: parseRichTextHtml(asString(field(source, 'contentHtml')) ?? '', ctx),
    publishedEpochSec: asEpochSeconds(field(source, 'published')),
    creatorHeart: parseCreatorHeart(field(source, 'creatorHeart'), ctx),
    replies: parseRepliesRef(field(source, 'replies')),
  });
}

export function parseCommentList(raw: unknown, ctx: ParseContext): ParsedList<Comment> {
  const array = asArray(raw);
  if (!array) return { items: [], dropped: [] };
  const items: Comment[] = [];
  const dropped: ParseError[] = [];
  for (const entry of array) {
    const parsed = parseComment(entry, ctx);
    if (parsed.ok) items.push(parsed.value);
    else dropped.push(parsed.error);
  }
  return { items, dropped };
}

/**
 * R4 — one page of comments *or* of replies. The replies response is the same object without
 * `commentCount`, so there is one parser rather than two, and `commentCount: null` is the honest
 * report for a replies page rather than a fabricated zero.
 *
 * A page whose `comments` array is missing entirely parses to an empty page rather than failing:
 * "no comments" is a real state (they can be disabled) and it must not read as an error.
 */
export function parseCommentPage(raw: unknown, ctx: ParseContext): Result<CommentPage, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('CommentPage', null, 'wrong-type'));

  const comments = parseCommentList(field(source, 'comments'), ctx);

  return ok({
    videoId: asNonEmptyString(field(source, 'videoId')),
    commentCount: asNumber(field(source, 'commentCount')),
    continuation: asNonEmptyString(field(source, 'continuation')),
    comments: comments.items,
    dropped: comments.dropped,
  });
}
